import { isRecetteBuild } from '@shared/constants/buildProfile';
import { logger } from '@infra/telemetry/Logger';
import { BackupService } from '@infra/backup/BackupService';
import { NexxusDatabase } from '@infra/persistence/NexxusDatabase';
import type { LicensingLatestRelease } from './NexxusUpdateManifest';
import { manifestFromLatestRelease, validateNexxusUpdateManifest } from './NexxusUpdatePackage';
import { verifyReleaseManifestJwt } from './PackageSignatureVerifier';
import { detectReleasePlatform, latestReleaseUrl } from './licensingApiUrl';
import { compareAppVersions, isSha256Hex } from '@shared/utils/compareAppVersions';
import { resilientFetch } from '@infra/network/resilientFetch';

export interface UpdateManifest {
  latest_version: string;
  current_version: string;
  is_outdated: boolean;
  is_critical: boolean;
  download_url: string;
  platform: string;
  architecture: string;
  signature_url: string;
  checksum_sha256: string;
  manifest_signature: string;
  changelog_url?: string;
  size_bytes?: number;
  published_at: string;
  local_schema_version?: number | null;
  api_compatibility?: string | null;
  /**
   * Mode d’installation :
   * - signed : manifeste dont la signature a été vérifiée par le processus principal Electron
   * - browser : ouverture de la page de téléchargement officielle (aucune installation automatique)
   */
  install_mode?: 'signed' | 'browser';
  /** Manifeste signé tel que reçu : transmis tel quel au processus principal, qui le revérifie. */
  signed_manifest?: unknown;
}

const OFFICIAL_DOWNLOAD_PAGE = 'https://nexxus.ga/telechargement-client';

/** Refus de sécurité : jamais de repli silencieux vers le navigateur. */
const REFUSAL_MARKERS = [
  'Empreinte SHA-256 obligatoire',
  'Manifeste signé requis',
  'Manifeste non signé',
  'Signature du manifeste invalide',
  'Manifeste de mise à jour invalide',
  'non strictement supérieure',
  'Checksum de mise à jour invalide',
  'URL de mise à jour non autorisée',
];

export const CDN_RELEASES_CLIENT_URL = 'https://updates.nexxus.ga/public/releases-client.json';

interface CdnReleasesClient {
  version: string;
  updated_at?: string;
  checksum_sha256?: string;
  platforms?: Partial<
    Record<
      'win' | 'mac' | 'linux',
      {
        available?: boolean;
        download_url?: string;
        label?: string;
        sha256?: string;
      }
    >
  >;
  /** Signature détachée (base64url) du manifeste canonique — vérifiée par le processus principal. */
  manifest_signature?: string;
  /** Manifeste PLAT du serveur (GET /v1/releases/latest) : un seul paquet pour une plateforme. */
  platform?: string;
  architecture?: string;
  download_url?: string;
  published_at?: string;
  mandatory?: boolean;
}

/**
 * Un manifeste est « signé » s'il porte une signature détachée non vide et soit la forme plate du
 * serveur (platform + download_url), soit un catalogue `platforms`. Seul le processus principal
 * décide de sa validité (signature, plateforme, sha256, version) ; ici on n'oriente que l'aiguillage.
 */
function isSignedManifestShape(value: unknown): value is CdnReleasesClient {
  if (!value || typeof value !== 'object') return false;
  const m = value as Record<string, unknown>;
  return (
    typeof m.manifest_signature === 'string' &&
    m.manifest_signature.length > 0 &&
    ((typeof m.platforms === 'object' && m.platforms !== null) ||
      (typeof m.platform === 'string' && typeof m.download_url === 'string'))
  );
}

export interface UpdateProgress {
  stage:
    | 'checking'
    | 'downloading'
    | 'verifying'
    | 'backing_up'
    | 'applying'
    | 'restarting'
    | 'downloaded'
    | 'done'
    | 'failed';
  percent?: number;
  receivedBytes?: number;
  totalBytes?: number;
  message?: string;
  upToDate?: boolean;
}

/**
 * En environnement web (PWA), c'est le Service Worker qui détecte la nouvelle
 * version et propose de la recharger. En environnement Electron, on télécharge
 * un .exe signé via electron-updater.
 */
export class UpdateService {
  private listeners: ((p: UpdateProgress) => void)[] = [];

  constructor(
    private readonly current_version: string,
    private readonly db: NexxusDatabase,
  ) {}

  onProgress(cb: (p: UpdateProgress) => void): void {
    this.listeners.push(cb);
  }

  report(p: UpdateProgress): void {
    this.emit(p);
  }

  private emit(p: UpdateProgress): void {
    this.listeners.forEach((l) => l(p));
  }

  /** Vérifie si une mise à jour est disponible (API signée + catalogue CDN). */
  async checkForUpdate(): Promise<UpdateManifest | null> {
    // Build de recette : aucune mise à jour (jamais confondu avec une version commerciale).
    if (isRecetteBuild()) return null;
    this.emit({ stage: 'checking' });
    try {
      const [apiManifest, cdn] = await Promise.all([
        this.fetchSignedApiManifest(),
        this.fetchCdnCatalog(),
      ]);

      // Toujours prendre la source la plus récente (CDN souvent à jour avant l’API licences).
      const candidates: UpdateManifest[] = [];
      if (apiManifest) candidates.push(apiManifest);
      if (cdn) {
        const fromCdn = await this.manifestFromCdn(cdn);
        if (fromCdn) candidates.push(fromCdn);
      }

      if (candidates.length === 0) {
        this.emit({ stage: 'failed', message: 'Aucune source de mise à jour joignable' });
        return null;
      }

      candidates.sort((a, b) => compareAppVersions(b.latest_version, a.latest_version));
      const best = candidates[0]!;
      // Préférer l’installation desktop (manifeste signé) à l’ouverture navigateur
      // à version égale.
      const sameVersion = candidates.filter(
        (c) => compareAppVersions(c.latest_version, best.latest_version) === 0,
      );
      const preferred = sameVersion.find((c) => c.install_mode === 'signed') ?? best;

      this.emit({
        stage: 'done',
        upToDate: !preferred.is_outdated,
        message: preferred.is_outdated
          ? `Mise à jour ${preferred.latest_version} disponible (actuelle : ${this.current_version})`
          : `Version ${this.current_version} — déjà à jour`,
      });
      return preferred;
    } catch (err) {
      this.emit({ stage: 'failed', message: String(err) });
      return null;
    }
  }

  /**
   * Fait vérifier un manifeste signé par le processus principal (clé publique embarquée).
   * Hors Electron, ou sans vérification possible : null (jamais d'installation automatique).
   */
  private async verifyInMainProcess(
    manifest: unknown,
  ): Promise<ElectronManifestVerification | null> {
    if (!window.electron?.verifyUpdateManifest || !window.electron.downloadUpdate) return null;
    try {
      const result = await window.electron.verifyUpdateManifest(manifest);
      if (!result.ok) {
        logger.warn({ event: 'update_manifest_refused', error: result.error });
        return null;
      }
      return result;
    } catch {
      return null;
    }
  }

  private fromVerifiedManifest(
    raw: CdnReleasesClient,
    verified: ElectronManifestVerification,
  ): UpdateManifest {
    const platform = detectReleasePlatform();
    const version = verified.version ?? raw.version;
    return {
      latest_version: version,
      current_version: this.current_version,
      is_outdated: compareAppVersions(this.current_version, version) < 0,
      is_critical: Boolean(verified.mandatory),
      download_url: verified.url ?? '',
      platform,
      architecture:
        typeof raw.architecture === 'string' && raw.architecture
          ? raw.architecture
          : platform === 'mac'
            ? 'universal'
            : 'x64',
      signature_url: '',
      checksum_sha256: verified.sha256 ?? '',
      manifest_signature: raw.manifest_signature ?? '',
      size_bytes: verified.sizeBytes || undefined,
      published_at: raw.published_at ?? raw.updated_at ?? new Date().toISOString(),
      install_mode: 'signed',
      signed_manifest: raw,
    };
  }

  private async fetchSignedApiManifest(): Promise<UpdateManifest | null> {
    try {
      const res = await resilientFetch(latestReleaseUrl(), {}, { timeoutMs: 10_000, retries: 1 });
      if (!res.ok) return null;
      const body = (await res.json()) as unknown;

      // Manifeste signé (forme plate du serveur, ou catalogue `platforms`) : revérifié par le
      // processus principal (signature détachée ES256 sur le JSON canonique, plateforme, sha256).
      if (isSignedManifestShape(body)) {
        const verified = await this.verifyInMainProcess(body);
        if (verified) return this.fromVerifiedManifest(body, verified);
        // Non vérifiable (hors Electron, signature/plateforme/sha256 refusés) : jamais
        // d'installation automatique ; on retombe sur l'information seule (page officielle).
      }

      // Ancien format plat (JWT) : informatif uniquement. Le processus principal ne peut pas le
      // revérifier, donc aucune installation automatique — page de téléchargement officielle.
      const data = body as LicensingLatestRelease;
      if (!data?.version || !data.download_url) return null;
      const pkg = manifestFromLatestRelease(data);
      const validation = validateNexxusUpdateManifest(pkg);
      if (!validation.valid) {
        logger.warn({
          event: 'update_api_manifest_invalid',
          version: data.version,
          errors: validation.errors,
        });
        return null;
      }
      const sig = await verifyReleaseManifestJwt(pkg.manifest_signature, {
        version: data.version,
        platform: data.platform,
        architecture: data.architecture,
        download_url: data.download_url,
        checksum_sha256: data.checksum_sha256,
        mandatory: data.mandatory,
        published_at: data.published_at,
        local_schema_version: data.local_schema_version,
        api_compatibility: data.api_compatibility,
      });
      if (!sig.valid) {
        logger.warn({
          event: 'update_api_signature_invalid',
          version: data.version,
          error: sig.error,
        });
        return null;
      }
      const manifest: UpdateManifest = {
        latest_version: data.version,
        current_version: this.current_version,
        is_outdated: compareAppVersions(this.current_version, data.version) < 0,
        is_critical: data.mandatory,
        download_url: OFFICIAL_DOWNLOAD_PAGE,
        platform: data.platform,
        architecture: data.architecture,
        signature_url: data.signature_url ?? '',
        checksum_sha256: data.checksum_sha256,
        manifest_signature: pkg.manifest_signature,
        published_at: data.published_at,
        local_schema_version: data.local_schema_version,
        api_compatibility: data.api_compatibility,
        install_mode: 'browser',
      };
      if (data.release_notes) manifest.changelog_url = data.release_notes;
      return manifest;
    } catch {
      return null;
    }
  }

  private async fetchCdnCatalog(): Promise<CdnReleasesClient | null> {
    try {
      const res = await resilientFetch(
        CDN_RELEASES_CLIENT_URL,
        { cache: 'no-store' },
        { timeoutMs: 10_000, retries: 1 },
      );
      if (!res.ok) return null;
      return (await res.json()) as CdnReleasesClient;
    } catch {
      return null;
    }
  }

  private async manifestFromCdn(cdn: CdnReleasesClient): Promise<UpdateManifest | null> {
    if (!cdn.version) return null;
    if (isSignedManifestShape(cdn)) {
      const verified = await this.verifyInMainProcess(cdn);
      if (verified) return this.fromVerifiedManifest(cdn, verified);
    }
    // Catalogue non signé / non vérifiable : information seulement, jamais d'installation directe
    // (ni SHA-256 « sidecar » : non signé donc non fiable).
    const platform = detectReleasePlatform();
    return {
      latest_version: cdn.version,
      current_version: this.current_version,
      is_outdated: compareAppVersions(this.current_version, cdn.version) < 0,
      is_critical: false,
      download_url: OFFICIAL_DOWNLOAD_PAGE,
      platform,
      architecture: platform === 'mac' ? 'universal' : 'x64',
      signature_url: '',
      checksum_sha256: '',
      manifest_signature: '',
      published_at: cdn.updated_at ?? new Date().toISOString(),
      install_mode: 'browser',
    };
  }

  /** @deprecated Utiliser runBackupExport (chiffré) via UpdateCoordinator. */
  async backupBeforeUpdate(tenant_id: string, password: string): Promise<Blob> {
    this.emit({ stage: 'backing_up' });
    const backup = new BackupService(this.db);
    const bundle = await backup.export(tenant_id);
    return backup.toBlob(bundle, password);
  }

  /**
   * Lance la mise à jour. En environnement web, demande au SW d'activer la nouvelle version.
   * En environnement Electron, télécharge et applique l'installeur signé.
   */
  async applyUpdate(
    manifest: UpdateManifest,
    _tenant_id: string,
    options?: { skipBackup?: boolean },
  ): Promise<boolean> {
    try {
      if (!options?.skipBackup) {
        logger.warn({ event: 'update_unencrypted_backup_skipped' });
      }

      if (manifest.install_mode === 'browser') {
        this.emit({
          stage: 'downloading',
          percent: 50,
          message: 'Ouverture du téléchargement officiel Nexxus…',
        });
        const url = manifest.download_url || 'https://nexxus.ga/telechargement-client';
        if (window.electron?.openExternal) {
          await window.electron.openExternal(url);
        } else {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
        this.emit({
          stage: 'done',
          percent: 100,
          message: 'Téléchargez et installez la nouvelle version, puis relancez Nexxus ERP.',
        });
        return true;
      }

      if (manifest.install_mode === 'signed' && window.electron?.downloadUpdate) {
        if (!manifest.signed_manifest) {
          throw new Error('Manifeste signé requis avant toute installation');
        }
        this.emit({
          stage: 'downloading',
          percent: 0,
          receivedBytes: 0,
          totalBytes: manifest.size_bytes,
          message: 'Téléchargement de la mise à jour…',
        });
        const stopProgress = window.electron.onDownloadProgress?.((payload) => {
          const received = Number(payload.received) || 0;
          const total = Number(payload.total) || manifest.size_bytes || 0;
          const parsed = Number(payload.percent);
          const percent = Number.isFinite(parsed)
            ? Math.max(0, Math.min(100, parsed))
            : total > 0
              ? Math.min(99, Math.round((received / total) * 100))
              : 0;
          this.emit({
            stage: 'downloading',
            percent,
            receivedBytes: received,
            totalBytes: total,
            message: 'Téléchargement de la mise à jour…',
          });
        });
        if (!isSha256Hex(manifest.checksum_sha256)) {
          throw new Error(
            'Empreinte SHA-256 obligatoire avant toute installation. Téléchargez l’installateur depuis le site Nexxus.',
          );
        }
        try {
          // Le processus principal revérifie la signature ; URL, SHA-256 et version viennent du manifeste.
          const result = await window.electron.downloadUpdate(manifest.signed_manifest);
          if (!result.ok) throw new Error(result.error || 'Téléchargement vérifié impossible');
        } finally {
          stopProgress?.();
        }
        this.emit({ stage: 'verifying', percent: 100, message: 'Contrôle d’intégrité terminé' });
        const mac = manifest.platform === 'mac' || manifest.platform === 'darwin';
        this.emit({
          stage: 'downloaded',
          percent: 100,
          message: mac
            ? 'Installateur .dmg téléchargé. Ouvrez-le, glissez Nexxus dans Applications, puis relancez.'
            : 'Installateur téléchargé — lancement de l’installation.',
        });
        this.emit({
          stage: 'applying',
          percent: 100,
          message: mac
            ? 'Sur macOS, l’application ne s’installe pas toute seule : ouvrez le .dmg téléchargé.'
            : 'Lancement de l’installateur…',
        });
        this.emit({
          stage: 'restarting',
          percent: 100,
          message: mac
            ? 'Après copie dans Applications, relancez Nexxus ERP. Vos données restent dans le dossier utilisateur.'
            : 'Suivez l’installateur puis relancez Nexxus ERP. Vos données restent dans le dossier utilisateur.',
        });
        return true;
      }

      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg?.waiting) {
          this.emit({ stage: 'applying' });
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          this.emit({ stage: 'restarting' });
          setTimeout(() => location.reload(), 800);
          return true;
        }
      }

      this.emit({ stage: 'failed', message: 'Aucun mécanisme de mise à jour disponible' });
      return false;
    } catch (err) {
      logger.error({ event: 'update_failed', error: String(err) });
      const msg = String(err);
      // Pas de repli navigateur pour les refus de sécurité (checksum / URL).
      if (REFUSAL_MARKERS.some((marker) => msg.includes(marker))) {
        this.emit({ stage: 'failed', message: msg });
        return false;
      }
      // Repli : ouvrir la page de téléchargement officielle plutôt que laisser l’utilisateur bloqué.
      try {
        const fallback = 'https://nexxus.ga/telechargement-client';
        this.emit({
          stage: 'downloading',
          percent: 50,
          message: 'Installation directe impossible — ouverture du téléchargement officiel…',
        });
        if (window.electron?.openExternal) {
          await window.electron.openExternal(fallback);
        } else {
          window.open(fallback, '_blank', 'noopener,noreferrer');
        }
        this.emit({
          stage: 'done',
          percent: 100,
          message:
            'Téléchargez la dernière version sur le site Nexxus, installez-la, puis relancez l’ERP.',
        });
        return true;
      } catch {
        this.emit({ stage: 'failed', message: msg });
        return false;
      }
    }
  }
}
